/**
 * まなびモードの教科コイン報酬(docs/10_paltown_platform.md §4.3)。
 *
 * **コインを実際に増やせるのは Cloud Functions `submitQuizResult` だけ**
 * (CLAUDE.md ルール2)。クライアントは「1単元ぶんの成績を送って、
 * 返ってきた付与額と残高を表示する」ことしかしない。星の判定もサーバーが正で、
 * ここから送る `stars` は監査用の申告にすぎない。
 *
 * ローカルモード(Firebase 未設定)では送信先そのものが無いので、
 * `NoQuizRewardService` が `null` を返し、
 * **画面はコインの表示を丸ごと出さない**(「0コイン」とも出さない)。
 *
 * ここは `factory.ts` から静的に読まれるので、`@paltyping/quiz` は
 * **型としてすら参照しない**(帯・教科は素の文字列で扱う)。値を import すると
 * まなびの遅延読込チャンクが初期バンドルに載ってしまう(docs/10 §2)。
 */

/** 1単元 = 1セッション分の送信内容。 */
export interface QuizSessionSubmission {
  /** 帯(`kinder` / `e1` …)。 */
  readonly band: string;
  /** 教科(`かず` / `さんすう` …)。 */
  readonly subject: string;
  /**
   * セッションの種類。`'review'`(ふくしゅう)は単元に属さないので
   * `unitId` の代わりに `questionIds` を送る。省略は `'unit'`。
   */
  readonly mode?: 'unit' | 'review';
  /** 単元ID(ふくしゅう・残高問い合わせでは `null`)。 */
  readonly unitId: string | null;
  /**
   * ステージID(docs/13 §11。`{単元ID}-s{番号}`)。
   *
   * 省略するとサーバーは**その単元のステージ1**として扱う(ステージ制より前の
   * クライアントとの互換)。ふくしゅう・残高問い合わせでは送らない。
   */
  readonly stageId?: string;
  /**
   * 解いた問題ID(送った順)。
   *
   * ふくしゅうでは**必須**(単元IDの代わりの実在証明)。ステージプレイでは任意で、
   * 送るとサーバーが「そのステージの問題だけを解いたか」まで突き合わせる。
   */
  readonly questionIds?: readonly string[];
  readonly correct: number;
  readonly total: number;
  /** クライアントが表示した星(0〜3)。サーバーは計算し直す。 */
  readonly stars: number;
  /** といた時間の合計(ms)。 */
  readonly elapsedMs: number;
}

/** サーバーが返した付与結果。 */
export interface QuizRewardReceipt {
  /** 付与先の教科(送ったものと同じ)。 */
  readonly subject: string;
  /** 今回もらった教科コイン。 */
  readonly coinsEarned: number;
  /** 付与後のその教科のコイン残高。 */
  readonly balance: number;
  /** 1日の上限(教科の合計)で削られたか。 */
  readonly capped: boolean;
  /**
   * 報酬が出なかった理由(`'too-fast'` = 速すぎて対象外)。出たなら `null`。
   * `capped` と区別できるようにしてある(画面の出し分け・ログ用)。
   */
  readonly unrewarded: 'too-fast' | null;
  /**
   * 今回もらった**がんばりポイント**(docs/12 §2 のリーグ軸)。
   * コインの日次上限とは別の物差しなので、コインが止まってもこちらは入る。
   */
  readonly effortEarned: number;
}

/**
 * まなび報酬の窓口。
 *
 * どのメソッドも **`null` = 「わからない / 送らない」**。
 * 呼び出し側は `null` のときコイン表示そのものを出さない
 * (子どもの画面に「0」や「エラー」を出して体験を止めないため)。
 */
export interface QuizRewardService {
  /**
   * 単元1回ぶんの成績を送って付与結果を受け取る。
   *
   * 通信できなければ静かに保留して `null` を返す(次回の送信でまとめて流す)。
   * **リザルト画面はこの結果を待たない**。返ってきたら差し替えるだけ。
   */
  submitUnit(input: QuizSessionSubmission): Promise<QuizRewardReceipt | null>;
  /** いまのその教科のコイン残高(不明・ローカルモードは `null`)。 */
  balance(band: string, subject: string): Promise<number | null>;
}

/**
 * ローカルモード用の何もしない実装。
 *
 * まなびモードは Firebase 未設定でも普通に遊べる(進捗は localStorage)。
 * コインだけが付かないので、画面はコイン表示を隠す。
 */
export class NoQuizRewardService implements QuizRewardService {
  async submitUnit(): Promise<QuizRewardReceipt | null> {
    return null;
  }

  async balance(): Promise<number | null> {
    return null;
  }
}
